import { assertValue } from '@shared/helpers'
import { LoanOfferMode, LoanOfferType } from '@shared/sec-loans'
import { IntlShape } from 'react-intl'

type LoanOfferLabels = {
  title: string
  description: string
}

const resolvetTitle = ({
  loanOfferType,
  formatMessage,
}: {
  loanOfferType: LoanOfferType
  formatMessage: IntlShape['formatMessage']
}): string => {
  switch (loanOfferType) {
    case LoanOfferType.HousingLoan:
    case LoanOfferType.ConstructionLoan:
    case LoanOfferType.RenovationLoan: {
      return formatMessage({ id: 'loan-offer-loan-offer-type-housing-loan-label' })
    }
    case LoanOfferType.CottageLoan: {
      return formatMessage({ id: 'loan-offer-loan-offer-type-cottage-loan-label' })
    }
    case LoanOfferType.InvestmentLoan: {
      return formatMessage({ id: 'loan-offer-loan-offer-type-investment-loan-label' })
    }
    case LoanOfferType.ConsumptionLoan: {
      return formatMessage({ id: 'loan-offer-loan-offer-type-consumption-loan-label' })
    }
  }
}

const resolveDescription = ({
  loanOfferType,
  isLoanTransfer,
  formatMessage,
}: {
  loanOfferType: LoanOfferType
  isLoanTransfer: boolean
  formatMessage: IntlShape['formatMessage']
}) => {
  if (loanOfferType === LoanOfferType.ConstructionLoan) {
    return formatMessage({ id: 'loan-offer-loan-offer-type-construction-loan-label' })
  }
  if (loanOfferType === LoanOfferType.RenovationLoan) {
    return formatMessage({ id: 'loan-offer-loan-offer-type-renovation-loan-label' })
  }
  if (loanOfferType === LoanOfferType.ConsumptionLoan && !isLoanTransfer) {
    return formatMessage({ id: 'common-other' })
  }
  return isLoanTransfer
    ? formatMessage({ id: 'loan-offer-loan-offer-purpose-loan-transfer-label' })
    : formatMessage({ id: 'loan-offer-loan-offer-purpose-property-purcahse-label' })
}

export const resolveLoanOfferLabels = ({
  offeredLoansAmount,
  firstLoanOfferType,
  isFirstOfferLoanTransfer,
  formatMessage,
}: {
  offeredLoansAmount: number
  firstLoanOfferType?: LoanOfferType
  isFirstOfferLoanTransfer?: boolean
  formatMessage: IntlShape['formatMessage']
}): LoanOfferLabels => {
  if (offeredLoansAmount > 1) {
    return {
      title: formatMessage({ id: 'loan-offer-loan-offer-type-multiple-loans-label' }),
      description: formatMessage(
        { id: 'loan-offer-loan-offer-type-multiple-loans-description' },
        { amount: offeredLoansAmount },
      ),
    }
  }
  const loanOfferType = assertValue(firstLoanOfferType, 'firstLoanOfferType')
  const isLoanTransfer = assertValue(isFirstOfferLoanTransfer, 'isFirstOfferLoanTransfer')
  return {
    title: resolvetTitle({ loanOfferType, formatMessage }),
    description: resolveDescription({
      loanOfferType,
      isLoanTransfer,
      formatMessage,
    }),
  }
}

type TheEndLabels = {
  title: string
  leadText: string | null
}

export const resolveTheEndLabels = ({
  loanOfferMode,
  hasUsedNetbankRecently,
  formatMessage,
}: {
  loanOfferMode: LoanOfferMode
  hasUsedNetbankRecently: boolean
  formatMessage: IntlShape['formatMessage']
}): TheEndLabels => {
  if (loanOfferMode === LoanOfferMode.Manual && hasUsedNetbankRecently) {
    return {
      title: formatMessage({ id: 'loan-offer-the-end-step-happens-next' }),
      leadText: formatMessage({ id: 'loan-offer-the-end-step-description-conversation' }),
    }
  }
  const labelsMap: Record<LoanOfferMode, TheEndLabels> = {
    [LoanOfferMode.Automatic]: {
      title: formatMessage({ id: 'loan-offer-the-end-step-happens-next' }),
      leadText: formatMessage({ id: 'loan-offer-the-end-step-description-contact' }),
    },
    [LoanOfferMode.Manual]: {
      title: formatMessage({ id: 'loan-offer-the-end-step-thank-you-heading' }),
      leadText: null,
    },
  }
  return labelsMap[loanOfferMode]
}
